import React, { useState } from 'react';
import { Box, IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MoreHorizOutlined from '@mui/icons-material/MoreHorizOutlined';

const AdminMenu = ({ items }) => {
  const { ft } = useTheme().palette;
  const [anchorEl, setAnchorEl] = useState(null);

  const handleItem = (item) => {
    setAnchorEl(null);
    item.onClick?.();
  };

  return (
    <>
      <Tooltip title="More actions">
        <IconButton
          aria-label="More actions"
          aria-haspopup="menu"
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={{ color: ft.neutrals.textSecondary }}
        >
          <MoreHorizOutlined />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {items.map((item) => (
          <MenuItem
            key={item.label}
            disabled={item.disabled}
            onClick={() => handleItem(item)}
            sx={{ fontSize: 14, color: ft.neutrals.textPrimary }}
          >
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

/**
 * PageToolbar — standard toolbar row (UI Design Standards §3.2): filters on
 * the left, an optional status caption, actions on the right, then a ⋯ menu
 * for admin actions. Layout only; it does not own filter state.
 *
 * Props:
 *   filters       {node}  usually FormFields
 *   status        {node}  e.g. "Last ACC sync …" caption
 *   actions       {node}  buttons
 *   adminActions  {Array<{ label, onClick, disabled }>}  ⋯ menu items
 */
const PageToolbar = ({ filters, status, actions, adminActions = [], sx }) => {
  const { ft } = useTheme().palette;
  const hasAdmin = adminActions.length > 0;

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-end',
        gap: 2,
        px: 3,
        py: 2,
        '& .MuiButton-root': { textTransform: 'none', fontWeight: 500 },
        ...sx,
      }}
    >
      {filters}
      {status && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            minHeight: 40,
            fontSize: 12,
            color: ft.neutrals.textSecondary,
          }}
        >
          {status}
        </Box>
      )}
      {(actions || hasAdmin) && (
        <Box
          sx={{
            ml: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            minHeight: 40,
          }}
        >
          {actions}
          {hasAdmin && <AdminMenu items={adminActions} />}
        </Box>
      )}
    </Box>
  );
};

export default PageToolbar;
